import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Pill } from "@/components/StatusBadge";
import { Combobox } from "@/components/Combobox";
import { ShiftNoteAttachments } from "@/components/ShiftNoteAttachments";
import { ReceiptThumb } from "@/components/ReceiptThumb";
import { LocationMap } from "@/components/MapPin";
import { fmtDistance } from "@/lib/geo";
import { AiReviewPanel, AiReviewBadge, AiAutoBadge } from "@/components/AiReviewPanel";
import { HoursPanel, AwakePanel, ExpensePanel, initReview, awakeStateFrom, buildBody, validateReview, pendingDecisions } from "@/components/timesheets/ShiftReviewPanels";
import { TS_STYLES } from "@/lib/status";
import { fmtTime, fmtDate, fmtDateTime, roundQuarter } from "@/lib/format";
import { Loader2, Check, Flag, Clock, Eye, AlertTriangle, RotateCcw, ExternalLink, ShieldAlert, MapPin, Sparkles, History, Save, AlarmClock, Download, X, Ban, CalendarRange, ChevronDown, ChevronRight, Link2, Moon, Undo2, Plus } from "lucide-react";
import { toast } from "sonner";
import { fmtLog, orgToday } from "@/lib/tz";

// Collapse linked legs (same link_group_id, same worker) into one group entry, placed
// where the group's first leg sits. Expanded groups are followed by their legs.
const legGroup = (r) => (r.link_group_id ? `${r.link_group_id}|${r.support_worker || ""}` : null);
function groupedRows(rows, expanded) {
  const byGroup = {};
  rows.forEach((r) => { const g = legGroup(r); if (g) (byGroup[g] = byGroup[g] || []).push(r); });
  const seen = new Set();
  const out = [];
  rows.forEach((r) => {
    const gid = legGroup(r);
    const legs = gid ? byGroup[gid] : null;
    if (!legs || legs.length < 2) { out.push({ s: r }); return; }
    if (seen.has(gid)) return;
    seen.add(gid);
    const sorted = [...legs].sort((a, b) => String(a.start_time || "").localeCompare(String(b.start_time || "")));
    out.push({ group: { id: gid, legs: sorted } });
    if (expanded.has(gid)) sorted.forEach((l, i) => out.push({ s: l, child: true, leg: i + 1, legCount: sorted.length }));
  });
  return out;
}

const dlBlob = (blob, fn) => { const u = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = u; a.download = fn; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u); };

// ── Date-range picker helpers ────────────────────────────────────────────────

const DR_SESSION_KEY = "ts_date_range";
// The Approved tab keeps its own range (default: last 90 days) so the other
// tabs never hide an old shift that still needs action.
const DR_APPROVED_KEY = "ts_date_range_approved";

const PRESETS = [
  { label: "Today",      getValue: () => { const d = dayjs().format("YYYY-MM-DD"); return { from: d, to: d, preset: "Today" }; } },
  { label: "This Week",  getValue: () => ({ from: dayjs().startOf("week").format("YYYY-MM-DD"), to: dayjs().endOf("week").format("YYYY-MM-DD"), preset: "This Week" }) },
  { label: "This Month", getValue: () => ({ from: dayjs().startOf("month").format("YYYY-MM-DD"), to: dayjs().endOf("month").format("YYYY-MM-DD"), preset: "This Month" }) },
  { label: "Last 90 days", getValue: () => ({ from: dayjs().subtract(90, "day").format("YYYY-MM-DD"), to: dayjs().format("YYYY-MM-DD"), preset: "Last 90 days" }) },
  { label: "All",        getValue: () => ({ from: "", to: "", preset: "All" }) },
];

function loadPersistedRange(key = DR_SESSION_KEY, fallback = "All") {
  const preset = (label) => PRESETS.find((p) => p.label === label);
  try {
    const raw = sessionStorage.getItem(key);
    if (raw) {
      const r = JSON.parse(raw);
      // Named presets are relative to today — recompute so they never go stale.
      return preset(r.preset) ? preset(r.preset).getValue() : r;
    }
  } catch { /* ignore */ }
  return preset(fallback).getValue();
}

function savePersistedRange(range, key = DR_SESSION_KEY) {
  try { sessionStorage.setItem(key, JSON.stringify(range)); } catch { /* ignore */ }
}

function fmtRangeLabel(range) {
  if (!range.from && !range.to) return "All dates";
  if (range.preset && range.preset !== "All" && range.preset !== "Custom") return range.preset;
  if (range.from && range.to) return `${range.from} → ${range.to}`;
  if (range.from) return `From ${range.from}`;
  return `To ${range.to}`;
}

function DateRangePicker({ value, onChange, storageKey = DR_SESSION_KEY }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ from: value.from, to: value.to });

  // Sync draft when popover opens
  const handleOpenChange = (o) => {
    if (o) setDraft({ from: value.from, to: value.to });
    setOpen(o);
  };

  const applyPreset = (preset) => {
    const next = preset.getValue();
    onChange(next);
    savePersistedRange(next, storageKey);
    setOpen(false);
  };

  const applyCustom = () => {
    const next = { from: draft.from, to: draft.to, preset: "Custom" };
    onChange(next);
    savePersistedRange(next, storageKey);
    setOpen(false);
  };

  const isActive = value.from || value.to;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant={isActive && value.preset !== "All" ? "default" : "outline"}
          className={`h-9 text-xs gap-1.5 ${isActive && value.preset !== "All" ? "bg-[#bf3b4f] hover:bg-[#a8303f] text-white border-transparent" : ""}`}
          data-testid="ts-date-range-trigger"
        >
          <CalendarRange className="h-3.5 w-3.5" />
          <span>{fmtRangeLabel(value)}</span>
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3 space-y-3" data-testid="ts-date-range-popover">
        {/* Preset chips */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Quick select</div>
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => applyPreset(p)}
                data-testid={`ts-dr-preset-${p.label.replace(/\s/g, "-")}`}
                className={`px-3 py-1 rounded-full text-[11px] font-semibold border transition-colors
                  ${value.preset === p.label
                    ? "bg-[#bf3b4f] text-white border-[#bf3b4f]"
                    : "border-slate-200 text-slate-600 hover:border-[#bf3b4f] hover:text-[#bf3b4f]"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        {/* Custom range */}
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">Custom range</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-500 mb-0.5 block">From</label>
              <Input
                type="date"
                value={draft.from}
                onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
                className="h-8 text-xs"
                data-testid="ts-dr-from"
              />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 mb-0.5 block">To</label>
              <Input
                type="date"
                value={draft.to}
                onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
                className="h-8 text-xs"
                data-testid="ts-dr-to"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-2 justify-end">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpen(false)} data-testid="ts-dr-cancel">
              Cancel
            </Button>
            <Button size="sm" className="h-7 text-xs" onClick={applyCustom} data-testid="ts-dr-apply">
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const TABS = ["Pending Review", "Returned", "Flagged", "Approved"];

/** Why an approved shift can't be unapproved (paid and/or invoiced), or "" if it can. */
const closedReason = (s) => {
  const paid = s.payroll_status === "Exported";
  const invoiced = ["Invoiced", "Submitted"].includes(s.custom_invoice_status);
  if (paid && invoiced) return "Paid and invoiced — void the pay run and the invoice to unapprove";
  if (paid) return "Paid — void the pay run to unapprove";
  if (invoiced) return "Invoiced — void the invoice to unapprove";
  return "";
};
const DISMISS_REASONS = ["Worker never attended", "Shift didn't go ahead",
  "Handled manually in payroll", "Duplicate / created in error", "Other"];
const OVERDUE_TAB = "Overdue";

const overdueLabel = (min) => {
  if (min == null) return "";
  if (min < 120) return `${Math.round(min)} min overdue`;
  const h = min / 60;
  if (h < 48) return `${Math.round(h)}h overdue`;
  return `${Math.round(h / 24)} days overdue`;
};

function NoteSections({ n }) {
  return (
    <div className="space-y-1">
      {n.urgent_notes && (
        <div><span className="text-[9px] font-bold uppercase tracking-wider text-amber-600">Important</span>
          <div className="whitespace-pre-wrap text-amber-700">{n.urgent_notes}</div></div>
      )}
      <div><span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Events</span>
        <div className="whitespace-pre-wrap text-slate-700">{n.events || <span className="text-red-400">empty</span>}</div></div>
      {n.mood && (
        <div><span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Mood</span>
          <div className="whitespace-pre-wrap text-slate-600">{n.mood}</div></div>
      )}
      {n.contacted_office ? (
        <div><span className="text-[9px] font-bold uppercase tracking-wider text-[#bf3b4f]">Contacted office</span>
          <div className="whitespace-pre-wrap text-[#bf3b4f]">{n.office_contact_reason || "Yes"}</div></div>
      ) : null}
      {(n.attachments || []).length > 0 && (
        <div><span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Attachments</span>
          <div className="mt-1"><ShiftNoteAttachments items={n.attachments} load={(idx, preview) => api.shiftNoteAttachment(n.name, idx, preview)} /></div></div>
      )}
    </div>
  );
}

function ManualKmForm({ shiftName, km, canEdit, canApprove, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ km_claimed: "", reason: "", start_address: "", end_address: "", extension_requested: false, extension_contact: "" });
  const authorised = Number(km?.authorised || 0);
  const over = Number(f.km_claimed || 0) > authorised && authorised > 0;
  const start = () => {
    setF({
      km_claimed: km?.claimed || "", reason: km?.reason || "",
      start_address: km?.start_address || "", end_address: km?.end_address || "",
      extension_requested: !!km?.extension_requested, extension_contact: km?.extension_contact || "",
    });
    setOpen(true);
  };
  const save = async () => {
    if (!(Number(f.km_claimed) > 0)) return toast.error("Enter a KM value greater than zero");
    if (!f.reason.trim()) return toast.error("A reason is required");
    if (over && f.extension_requested && !f.extension_contact.trim()) return toast.error("Note who authorised the extension");
    setBusy(true);
    try {
      await api.manualKm(shiftName, {
        km_claimed: Number(f.km_claimed), reason: f.reason.trim(),
        start_address: f.start_address.trim(), end_address: f.end_address.trim(),
        extension_requested: f.extension_requested, extension_contact: f.extension_contact.trim(),
      });
      toast.success("Manual KM claim saved");
      setOpen(false);
      await onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const claimed = Number(km?.claimed || 0);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const reject = async () => {
    if (!rejectReason.trim()) return toast.error("Enter a reason for rejecting the KM claim");
    setBusy(true);
    try {
      await api.rejectKm(shiftName, { reason: rejectReason.trim() });
      toast.success("KM claim rejected");
      setRejecting(false); setRejectReason("");
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reject KM claim"); }
    finally { setBusy(false); }
  };
  // KM charged to the participant: the worker is paid the whole claim, the company the rest.
  const [billing, setBilling] = useState(false);
  const [bill, setBill] = useState({ km: "", reason: "" });
  const charged = km?.billed ?? claimed;
  const startBill = () => { setBill({ km: String(charged), reason: km?.bill_reason || "" }); setBilling(true); };
  const saveBill = async () => {
    const v = Number(bill.km);
    if (bill.km === "" || !(v >= 0) || v > claimed) return toast.error(`Enter between 0 and ${claimed} km`);
    if (v < claimed && !bill.reason.trim()) return toast.error("A reason is required when the participant isn't charged for all the KM");
    setBusy(true);
    try {
      await api.billKm(shiftName, { km_billed: v, reason: bill.reason.trim() });
      toast.success("KM charge updated");
      setBilling(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update the KM charge"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs" data-testid="km-block">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">KM Claim{km?.source ? ` · ${km.source}` : ""}</div>
        {(canEdit || canApprove) && !open && (
          <div className="flex items-center gap-3">
            {canApprove && claimed > 0 && !billing && !rejecting && (
              <button onClick={startBill} className="text-[11px] font-semibold text-slate-500 hover:text-[#bf3b4f] hover:underline" data-testid="km-bill-toggle">Change KM charged</button>
            )}
            {canEdit && claimed > 0 && !rejecting && (
              <button onClick={() => setRejecting(true)} className="text-[11px] font-semibold text-slate-500 hover:text-red-600 hover:underline" data-testid="km-reject-toggle">Reject</button>
            )}
          {canEdit && (
          <button onClick={start} className="text-[11px] font-semibold text-[#bf3b4f] hover:underline" data-testid="km-manual-toggle">
            {claimed ? "Edit" : "Add manual claim"}
          </button>
          )}
          </div>
        )}
      </div>
      {!open && (
        <div className="mt-0.5 text-slate-700">
          {claimed ? (
            <>
              {km.start_odometer != null && <>Odometer {km.start_odometer} → {km.end_odometer ?? "—"} · </>}
              <b>{claimed} km</b> <span className="text-slate-400">(authorised {authorised})</span>
              {km.reason && <div className="text-slate-500 mt-0.5">Reason: {km.reason}</div>}
              {(km.start_address || km.end_address) && <div className="text-slate-500">{km.start_address || "?"} → {km.end_address || "?"}</div>}
              {km.billing && (
                km.billing.mode === "billed" ? (
                  <div className="text-emerald-700 mt-1 font-medium" data-testid="km-dest">Bills to Activity Based Transport · ${Number(km.billing.amount).toFixed(2)} ({km.billing.km}km × ${km.billing.rate}/km, billed as {Number(km.billing.units ?? km.billing.amount).toFixed(2)} units @ $1)</div>
                ) : km.billing.mode === "company_paid" ? (
                  <div className="text-slate-600 mt-1 font-medium" data-testid="km-dest">Not charged to the participant</div>
                ) : km.billing.mode === "company_expense" ? (
                  <div className="text-slate-600 mt-1 font-medium" data-testid="km-dest">Incurred company expense (SIL Weekly) — not billed</div>
                ) : (
                  <div className="text-amber-700 mt-1 font-medium" data-testid="km-dest">No Activity Based Transport item — company-absorbed</div>
                )
              )}
              {km.billed != null && (
                <div className="text-slate-700 mt-1" data-testid="km-company-paid">
                  Participant charged <b>{km.billed} km</b> · company pays <b>{Math.round((claimed - km.billed) * 10) / 10} km</b> · worker paid all {claimed} km
                  {km.bill_reason && <div className="text-slate-500 mt-0.5">Reason: {km.bill_reason}{km.bill_by ? ` (${km.bill_by})` : ""}</div>}
                </div>
              )}
              {claimed > authorised && (
                <div className="text-amber-700 mt-1 flex items-start gap-1"><AlertTriangle className="h-3 w-3 mt-0.5" />
                  Exceeds authorised. Extension: {km.extension_requested ? <b>Yes — spoke with {km.extension_contact || "?"}</b> : <b>No</b>}
                </div>
              )}
            </>
          ) : km?.rejected_km ? (
            <div className="text-red-700" data-testid="km-rejected">
              <b>{km.rejected_km} km rejected</b> — not paid or billed
              {km.reject_reason && <div className="text-slate-500 mt-0.5">Reason: {km.reject_reason}</div>}
            </div>
          ) : <span className="text-slate-400">No KM claim recorded.</span>}
          {billing && (
            <div className="mt-2 space-y-2" data-testid="km-bill-form">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-slate-500">KM to charge the participant</label>
                  <Input type="number" step="0.1" min="0" max={claimed} value={bill.km} onChange={(e) => setBill((s) => ({ ...s, km: e.target.value }))} className="h-8 text-xs mt-0.5" data-testid="km-bill-value" />
                </div>
                <div className="flex items-end"><span className="text-[10px] text-slate-400 pb-2">Worker is paid all {claimed} km · authorised {authorised}</span></div>
              </div>
              <Input value={bill.reason} onChange={(e) => setBill((s) => ({ ...s, reason: e.target.value }))} placeholder="Reason the company pays the rest (required if lower)" className="h-8 text-xs" data-testid="km-bill-reason" />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setBilling(false)}>Cancel</Button>
                <Button size="sm" onClick={saveBill} disabled={busy} data-testid="km-bill-save">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save KM charge"}</Button>
              </div>
            </div>
          )}
          {rejecting && (
            <div className="mt-2 space-y-2" data-testid="km-reject-form">
              <Input value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Reason for rejecting (required)" className="h-8 text-xs" data-testid="km-reject-reason" />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => { setRejecting(false); setRejectReason(""); }}>Cancel</Button>
                <Button size="sm" variant="destructive" onClick={reject} disabled={busy} data-testid="km-reject-confirm">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Reject KM claim"}</Button>
              </div>
            </div>
          )}
        </div>
      )}
      {open && (
        <div className="mt-2 space-y-2" data-testid="km-manual-form">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-500">KM travelled</label>
              <Input type="number" step="0.1" value={f.km_claimed} onChange={(e) => setF((s) => ({ ...s, km_claimed: e.target.value }))} className="h-8 text-xs mt-0.5" data-testid="km-manual-value" />
            </div>
            <div className="flex items-end"><span className="text-[10px] text-slate-400 pb-2">Authorised: {authorised} km</span></div>
          </div>
          <div>
            <label className="text-[10px] text-slate-500">Reason</label>
            <Input value={f.reason} onChange={(e) => setF((s) => ({ ...s, reason: e.target.value }))} placeholder="e.g. worker forgot to record drive" className="h-8 text-xs mt-0.5" data-testid="km-manual-reason" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-500">Start address (optional)</label>
              <Input value={f.start_address} onChange={(e) => setF((s) => ({ ...s, start_address: e.target.value }))} className="h-8 text-xs mt-0.5" data-testid="km-manual-start" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500">End address (optional)</label>
              <Input value={f.end_address} onChange={(e) => setF((s) => ({ ...s, end_address: e.target.value }))} className="h-8 text-xs mt-0.5" data-testid="km-manual-end" />
            </div>
          </div>
          {over && (
            <div className="rounded border border-amber-200 bg-amber-50 p-2">
              <label className="flex items-center gap-2 text-[11px] text-amber-800 cursor-pointer">
                <input type="checkbox" checked={f.extension_requested} onChange={(e) => setF((s) => ({ ...s, extension_requested: e.target.checked }))} data-testid="km-manual-ext" />
                Over authorised — extension approved
              </label>
              {f.extension_requested && (
                <Input value={f.extension_contact} onChange={(e) => setF((s) => ({ ...s, extension_contact: e.target.value }))} placeholder="Authorised by (name)" className="h-8 text-xs mt-1.5" data-testid="km-manual-ext-contact" />
              )}
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setOpen(false)} data-testid="km-manual-cancel">Cancel</Button>
            <Button size="sm" onClick={save} disabled={busy} data-testid="km-manual-save">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save KM claim"}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Timesheets() {
  const { can } = useAuth();
  const [tab, setTab] = useState("Pending Review");
  const [allRange, setAllRange] = useState(() => loadPersistedRange());
  const [approvedRange, setApprovedRange] = useState(() => loadPersistedRange(DR_APPROVED_KEY, "Last 90 days"));
  const [showClosed, setShowClosed] = useState(false);   // Approved tab: include paid / invoiced shifts
  const onApproved = tab === "Approved";
  const dateRange = onApproved ? approvedRange : allRange;
  const setDateRange = onApproved ? setApprovedRange : setAllRange;
  const rangeKey = onApproved ? DR_APPROVED_KEY : DR_SESSION_KEY;
  const [detail, setDetail] = useState(null);
  const [detailNotes, setDetailNotes] = useState([]);
  const [rows, setRows] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const toggleGroup = (id) => setExpandedGroups((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const [overdueRows, setOverdueRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unapprove, setUnapprove] = useState(null);
  const [unapproveReason, setUnapproveReason] = useState("");
  const [flag, setFlag] = useState(null);
  const [sendBack, setSendBack] = useState(null);
  const [reason, setReason] = useState("");
  const [detailExpenses, setDetailExpenses] = useState([]);
  const [detailMeta, setDetailMeta] = useState({ pt: false, km: null, groups: [], defaultGroup: "" });
  // Single review/approval state: approved pay + billed times, awake and expense decisions.
  const [review, setReview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [awakeEntries, setAwakeEntries] = useState([]);
  const [approving, setApproving] = useState(false);
  const [detailIncidents, setDetailIncidents] = useState([]);
  const [history, setHistory] = useState(null);
  const [returnParts, setReturnParts] = useState({ notes: [], km: false, expenses: false, incidents: false, tasks: false, awake: false });
  // What the office last sent back on a Returned timesheet (null otherwise).
  const [detailReturn, setDetailReturn] = useState(null);
  const today = orgToday();
  const [notesDl, setNotesDl] = useState({ open: false, date_from: dayjs(orgToday()).subtract(14, "day").format("YYYY-MM-DD"), date_to: today, participant: "" });
  const [notesDlParticipants, setNotesDlParticipants] = useState([]);
  const [notesDlBusy, setNotesDlBusy] = useState(false);

  const openNotesDl = async () => {
    setNotesDl((s) => ({ ...s, open: true }));
    if (notesDlParticipants.length === 0) {
      try { setNotesDlParticipants(await api.participants()); } catch { /* optional */ }
    }
  };
  const downloadNotes = async () => {
    setNotesDlBusy(true);
    try {
      const params = { date_from: notesDl.date_from, date_to: notesDl.date_to };
      if (notesDl.participant) params.participant = notesDl.participant;
      const blob = await api.bulkNotesDownload(params);
      dlBlob(blob, `ShiftNotes-${notesDl.date_from}_to_${notesDl.date_to}.pdf`);
      setNotesDl((s) => ({ ...s, open: false }));
    } catch (e) {
      // The PDF is requested as a blob, so the server's message arrives as one too.
      let msg = "Could not generate the notes PDF";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* keep the generic message */ }
      toast.error(msg);
    }
    finally { setNotesDlBusy(false); }
  };

  // Overdue tab: "dismissed" toggle + the office actions for shifts nobody
  // clocked in or out of.
  const [showDismissed, setShowDismissed] = useState(false);
  const [overdueAction, setOverdueAction] = useState(null);  // {mode:'times'|'dismiss'|'cancel', shift}
  const [odTimes, setOdTimes] = useState({ clock_in: "", clock_out: "", reason: "" });
  const [odDismiss, setOdDismiss] = useState({ preset: DISMISS_REASONS[0], note: "" });
  const [odCancel, setOdCancel] = useState({ reason: "", charge: false, initiator: "" });
  const [odBusy, setOdBusy] = useState(false);

  const load = () => {
    setLoading(true);
    const dr = tab === OVERDUE_TAB ? null : (dateRange.from || dateRange.to ? dateRange : null);
    const p = tab === OVERDUE_TAB ? api.overdueTimesheets(showDismissed).then(setOverdueRows)
                                  : api.timesheets(tab, dr, onApproved && showClosed).then(setRows);
    p.catch(() => toast.error("Load failed")).finally(() => setLoading(false));
    // Keep the overdue badge fresh regardless of the active tab.
    if (tab !== OVERDUE_TAB) api.overdueTimesheets().then(setOverdueRows).catch(() => {});
  };
  useEffect(load, [tab, showDismissed, dateRange, showClosed]); // eslint-disable-line

  const openOverdueAction = (mode, s) => {
    setOverdueAction({ mode, shift: s });
    if (mode === "times") {
      setOdTimes({
        clock_in: dayjs(s.actual_clock_in || s.start_time).format("YYYY-MM-DDTHH:mm"),
        clock_out: dayjs(s.actual_clock_out || s.end_time).format("YYYY-MM-DDTHH:mm"),
        reason: "",
      });
    }
    if (mode === "dismiss") setOdDismiss({ preset: DISMISS_REASONS[0], note: "" });
    if (mode === "cancel") setOdCancel({ reason: "", charge: false, initiator: "" });
  };

  const submitOverdueAction = async () => {
    const s = overdueAction?.shift;
    if (!s) return;
    setOdBusy(true);
    try {
      if (overdueAction.mode === "times") {
        if (!odTimes.reason.trim()) throw { response: { data: { detail: "A reason is required" } } };
        if (dayjs(odTimes.clock_out).diff(dayjs(odTimes.clock_in)) <= 0) throw { response: { data: { detail: "Clock out must be after clock in" } } };
        await api.editTimesheetTimes(s.name, {
          clock_in: odTimes.clock_in.replace("T", " ") + ":00",
          clock_out: odTimes.clock_out.replace("T", " ") + ":00",
          reason: odTimes.reason.trim(),
        });
        toast.success("Times recorded — the shift is now awaiting approval");
      } else if (overdueAction.mode === "dismiss") {
        const reason = [odDismiss.preset, odDismiss.note.trim()].filter(Boolean).join(" — ");
        await api.dismissOverdue(s.name, reason);
        toast.success("Cleared off the overdue list");
      } else if (overdueAction.mode === "cancel") {
        if (!odCancel.reason.trim()) throw { response: { data: { detail: "A cancellation reason is required" } } };
        if (!odCancel.initiator) throw { response: { data: { detail: "Please select who initiated this cancellation" } } };
        await api.cancel(s.name, odCancel.reason.trim(), odCancel.charge, odCancel.initiator);
        toast.success(odCancel.charge ? "Shift cancelled with a charge" : "Shift cancelled (no charge)");
      }
      setOverdueAction(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not complete that"); }
    finally { setOdBusy(false); }
  };

  const restoreOverdue = async (s) => {
    try { await api.restoreOverdue(s.name); toast.success("Back on the overdue list"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not restore"); }
  };

  const loadExpenses = async (name) => {
    try {
      const ex = await api.shiftExpenses(name);
      const meta = { pt: ex.provider_travel_available, km: ex.km, groups: ex.bill_groups || [], defaultGroup: ex.default_bill_group || "" };
      setDetailExpenses(ex.expenses); setDetailMeta(meta);
      return { expenses: ex.expenses, meta };
    } catch {
      setDetailExpenses([]); setDetailMeta({ pt: false, km: null, groups: [], defaultGroup: "" });
      return { expenses: [], meta: { defaultGroup: "" } };
    }
  };

  // `name` is the timesheet key: the shift, or "<shift>::<worker>" for another
  // worker's timesheet on a multi-worker shift. `tsKey` names the worker so the
  // review shows only their notes.
  const openDetail = async (name, tsKey) => {
    const { shift: loaded } = await api.shift(name);
    const shift = { ...loaded, ts_key: tsKey || name };
    try { setDetailNotes(await api.shiftNotes(shift.ts_key)); } catch { setDetailNotes([]); }
    const { expenses, meta } = await loadExpenses(name);
    try { setDetailIncidents(await api.shiftIncidents(shift.shift_name || name)); } catch { setDetailIncidents([]); }
    setDetailReturn(null);
    if (shift.timesheet_status === "Returned") {
      try { setDetailReturn(await api.shiftReturn(name)); } catch { /* summary is informational */ }
    }
    const r = initReview(shift, expenses, meta.defaultGroup);
    setSummary(null); setAwakeEntries([]);
    try {
      const sm = await api.previewTimesheet(name, buildBody(r, shift, [], { withReasons: false }));
      setSummary(sm); setAwakeEntries(sm.awake_entries || []);
      r.awake = awakeStateFrom(sm.awake_entries);
    } catch { /* preview is informational */ }
    setReview(r);
    setDetail(shift);
  };

  // Refresh the hours preview as the approver changes times or awake decisions.
  const editable = !!(detail && can.has("timesheets.edit") && detail.timesheet_status !== "Approved");
  useEffect(() => {
    if (!detail || !review || !editable) return undefined;
    const t = setTimeout(() => {
      api.previewTimesheet(detail.name, buildBody(review, detail, awakeEntries, { withReasons: false }))
        .then(setSummary).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [review?.pay_start, review?.pay_end, review?.bill_start, review?.bill_end, review?.awake]); // eslint-disable-line

  const doReturn = async () => {
    if (!reason.trim()) return toast.error("Reason required");
    const parts = { notes: returnParts.notes, km: returnParts.km, expenses: returnParts.expenses, incidents: returnParts.incidents, tasks: returnParts.tasks, awake: returnParts.awake };
    if (parts.notes.length === 0 && !parts.km && !parts.expenses && !parts.incidents && !parts.tasks && !parts.awake)
      return toast.error("Select at least one part to send back");
    try {
      const r = await api.returnShiftNotes(sendBack.name, reason.trim(), parts);
      toast.success(`Sent back to worker — ${r.parts_returned}`);
      setSendBack(null); setReason(""); setDetail(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Send back failed"); }
  };

  const openSendBack = (opts) => {
    const awakeOnly = !!opts?.awake;
    setSendBack(detail);
    if (detailReturn) {
      // Changing an existing return: start from what was sent back last time.
      // An older "everything" return has no record of parts, so tick all of it.
      const all = !!detailReturn.legacy;
      setReason(detailReturn.reason || "");
      setReturnParts({ notes: detailReturn.notes || [], km: all || !!detailReturn.km, expenses: all || !!detailReturn.expenses,
                       incidents: all || !!detailReturn.incidents, tasks: all ? !!detail?.tasks?.length : !!detailReturn.tasks,
                       awake: all ? !!detail?.is_sleepover : !!detailReturn.awake });
      return;
    }
    setReason("");
    setReturnParts({ notes: awakeOnly ? [] : detailNotes.filter((n) => n.status === "Submitted").map((n) => n.name),
                     km: false, expenses: false, incidents: false, tasks: false, awake: awakeOnly });
  };
  // Notes the office can pick in the send-back dialog: submitted ones, plus
  // ones already sent back when changing a return.
  const returnableNotes = detailNotes.filter((n) => n.status === "Submitted" || (detail?.timesheet_status === "Returned" && n.status === "Returned"));
  const returnLabels = [["km", "KM claim"], ["expenses", "Expense claims"], ["incidents", "Incident report (ask for one)"], ["tasks", "Tasks"], ["awake", "Awake time"]];

  const doApprove = async () => {
    const err = validateReview(review, detail, detailExpenses, detailMeta);
    if (err) return toast.error(err);
    const pending = detailExpenses.filter((e) => e.status === "Pending");
    setApproving(true);
    try {
      await api.approveTimesheet(detail.name, {
        ...buildBody(review, detail, awakeEntries),
        expenses: pending.map((e) => ({ idx: e.idx, ...review.expDec[e.idx] })),
      });
      toast.success("Timesheet approved");
      setDetail(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setApproving(false); }
  };

  const doUnapprove = async () => {
    if (!unapproveReason.trim()) return toast.error("Reason required");
    try {
      await api.unapproveTimesheet(unapprove.name, unapproveReason.trim());
      toast.success("Timesheet unapproved — back in Pending Review");
      setUnapprove(null); setUnapproveReason(""); setDetail(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Unapprove failed"); }
  };

  const doFlag = async () => {
    if (!reason.trim()) return toast.error("Reason required");
    try { await api.flagTimesheet(flag.name, reason.trim()); toast.success("Flagged for review"); setFlag(null); setReason(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Flag failed"); }
  };

  const [aiBusy, setAiBusy] = useState(false);
  const runAiPending = async () => {
    setAiBusy(true);
    try { const r = await api.aiReviewPending(false); toast.success(`AI reviewed ${r.reviewed} shift(s)`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "AI batch failed"); }
    finally { setAiBusy(false); }
  };

  return (
    <div className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">Approval Workbench</h1>
          <p className="text-xs text-slate-500 mt-0.5">Review and approve each shift: pay comes from the approved clock times (rounded to 15 minutes), billing from the rostered times.</p>
        </div>
        {can.has("timesheets.edit") && (
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="outline" className="h-9 text-xs" onClick={openNotesDl} data-testid="bulk-notes-download-btn">
              <Download className="h-3.5 w-3.5 mr-1.5" />Download shift notes
            </Button>
            <Button size="sm" variant="outline" className="h-9 text-xs" onClick={runAiPending} disabled={aiBusy} data-testid="ai-run-pending">
              {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}Run AI on pending
            </Button>
          </div>
        )}
      </div>

      {tab !== OVERDUE_TAB && (
        <div className="flex items-center gap-2 mt-3 mb-1" data-testid="ts-date-range-bar">
          <span className="text-[11px] text-slate-500 font-medium shrink-0">Shift date:</span>
          <DateRangePicker value={dateRange} onChange={(r) => { setDateRange(r); }} storageKey={rangeKey} />
          {(dateRange.from || dateRange.to) && dateRange.preset !== "All" && (
            <button
              onClick={() => { const r = { from: "", to: "", preset: "All" }; setDateRange(r); savePersistedRange(r, rangeKey); }}
              className="text-[11px] text-slate-400 hover:text-slate-600 underline"
              data-testid="ts-dr-clear"
            >
              Clear filter
            </button>
          )}
          {onApproved && (
            <label className="ml-3 flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer" data-testid="ts-show-closed">
              <input type="checkbox" className="h-3.5 w-3.5 accent-[#bf3b4f]" checked={showClosed} onChange={(e) => setShowClosed(e.target.checked)} />
              Show paid &amp; invoiced
            </label>
          )}
        </div>
      )}

      <div className="flex gap-1 mt-4 mb-3 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`ts-tab-${t.replace(/\s/g, "-")}`}
            className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors ${tab === t ? "border-[#bf3b4f] text-[#bf3b4f]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {t}
          </button>
        ))}
        <button onClick={() => setTab(OVERDUE_TAB)} data-testid="ts-tab-Overdue"
          className={`px-3 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${tab === OVERDUE_TAB ? "border-amber-600 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          <AlarmClock className="h-3.5 w-3.5" /> Overdue
          {overdueRows.length > 0 && (
            <span data-testid="ts-overdue-count"
              className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold tabular-nums">
              {overdueRows.length}
            </span>
          )}
        </button>
      </div>

      {tab === OVERDUE_TAB ? (
        loading ? <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
          <div data-testid="ts-overdue-wrap">
          <div className="flex items-center justify-end mb-2">
            <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer" data-testid="ts-overdue-dismissed-toggle">
              <input type="checkbox" checked={showDismissed} onChange={(e) => setShowDismissed(e.target.checked)} className="h-3.5 w-3.5" />
              Show dismissed entries
            </label>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="ts-overdue-table">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="text-left px-3 py-2 font-bold">Shift</th>
                  <th className="text-left px-3 py-2 font-bold">Worker / Participant</th>
                  <th className="text-left px-3 py-2 font-bold">Scheduled</th>
                  <th className="text-left px-3 py-2 font-bold">Clock in/out</th>
                  <th className="text-left px-3 py-2 font-bold">Overdue</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {overdueRows.length === 0 && <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-400" data-testid="ts-overdue-empty">{showDismissed ? "Nothing has been dismissed." : "Nothing overdue — every finished shift has a submitted timesheet. 🎉"}</td></tr>}
                {overdueRows.map((s) => (
                  <tr key={s.name} className="border-b border-slate-50 hover:bg-amber-50/40" data-testid={`ts-overdue-row-${s.name}`}>
                    <td className="px-3 py-2"><div className="font-mono text-[11px] text-slate-500">{s.shift_name || s.name}</div><div className="text-slate-400 text-[10px]">{fmtDate(s.shift_date)}</div>{s.multi_worker && <div className="text-[10px] font-semibold text-violet-600" data-testid={`ts-multi-${s.name}`}>Multi-worker shift</div>}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800">{s.support_worker_name || "—"}</div>
                      <div className="text-slate-400 text-[10px]">{s.participant_name}</div>
                      {s.overdue_dismissed ? (
                        <div className="text-[10px] text-slate-500 mt-0.5" data-testid={`ts-overdue-dismissed-${s.name}`}>
                          Dismissed: “{s.overdue_dismissed_reason}” · {s.overdue_dismissed_by} · {fmtLog(s.overdue_dismissed_at)}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-slate-600">{fmtTime(s.start_time)}–{fmtTime(s.end_time)}</td>
                    <td className="px-3 py-2 tabular-nums text-slate-600">
                      {s.actual_clock_in ? fmtTime(s.actual_clock_in) : <span className="text-red-500 font-semibold">never clocked in</span>}
                      {s.actual_clock_in && <>–{s.actual_clock_out ? fmtTime(s.actual_clock_out) : <span className="text-red-500 font-semibold">missing</span>}</>}
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-semibold bg-amber-100 text-amber-800" data-testid={`ts-overdue-by-${s.name}`}>
                        <AlarmClock className="h-3 w-3" />{overdueLabel(s.overdue_minutes)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1 justify-end">
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => openDetail(s.name, s.ts_key)} data-testid={`ts-overdue-view-${s.name}`}>
                          <Eye className="h-3 w-3 mr-1" />View
                        </Button>
                        {can.has("timesheets.edit") && !showDismissed && (<>
                          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => openOverdueAction("times", s)} data-testid={`ts-overdue-times-${s.name}`}>
                            <Clock className="h-3 w-3 mr-1" />Enter times
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => openOverdueAction("dismiss", s)} data-testid={`ts-overdue-dismiss-${s.name}`}>
                            <X className="h-3 w-3 mr-1" />Dismiss
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-600 border-red-200" onClick={() => openOverdueAction("cancel", s)} data-testid={`ts-overdue-cancel-${s.name}`}>
                            <Ban className="h-3 w-3 mr-1" />Cancel shift
                          </Button>
                        </>)}
                        {can.has("timesheets.edit") && showDismissed && (
                          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => restoreOverdue(s)} data-testid={`ts-overdue-restore-${s.name}`}>
                            <RotateCcw className="h-3 w-3 mr-1" />Restore
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        )
      ) : loading ? <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="text-left px-3 py-2 font-bold">Shift</th>
                <th className="text-left px-3 py-2 font-bold">Worker / Participant</th>
                <th className="text-left px-3 py-2 font-bold">Rostered</th>
                <th className="text-left px-3 py-2 font-bold">Actual clock</th>
                <th className="text-left px-3 py-2 font-bold">Pay (15m)</th>
                <th className="text-left px-3 py-2 font-bold">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="px-3 py-10 text-center text-slate-400">No timesheets in “{tab}”.</td></tr>}
              {groupedRows(rows, expandedGroups).map(({ s, group, child, leg, legCount }) => {
                if (group) {
                  const legs = group.legs;
                  const open = expandedGroups.has(group.id);
                  const counts = {};
                  legs.forEach((l) => { counts[l.timesheet_status] = (counts[l.timesheet_status] || 0) + 1; });
                  const workers = [...new Set(legs.map((l) => l.support_worker_name).filter(Boolean))];
                  const names = [...new Set(legs.map((l) => l.participant_name).filter(Boolean))];
                  const missingNotes = legs.filter((l) => !l.notes_ready).length;
                  const noOut = legs.filter((l) => !l.actual_clock_out).length;
                  const incidents = legs.filter((l) => l.has_incident).length;
                  return (
                    <tr key={`grp-${group.id}`} className="border-b border-slate-100 bg-violet-50/50 hover:bg-violet-50 cursor-pointer" onClick={() => toggleGroup(group.id)} data-testid={`ts-group-${group.id}`}>
                      <td className="px-3 py-2"><div className="font-mono text-[11px] text-slate-500">{legs[0].shift_name || legs[0].name}{legs.length > 1 ? ` +${legs.length - 1}` : ""}</div><div className="text-slate-400 text-[10px]">{fmtDate(legs[0].shift_date)}</div></td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800">{workers.join(", ") || "—"}</div>
                        <div className="text-slate-400 text-[10px]">{names.join(" · ")}</div>
                        <div className="flex flex-wrap items-center gap-1.5 mt-0.5 text-[10px] font-semibold">
                          {missingNotes > 0 && <span className="text-red-500">{missingNotes} without notes</span>}
                          {noOut > 0 && <span className="text-red-500">{noOut} missing clock-out</span>}
                          {incidents > 0 && <span className="text-red-600">{incidents} incident{incidents > 1 ? "s" : ""}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 tabular-nums text-slate-600">{fmtTime(legs[0].start_time)}–{fmtTime(legs[legs.length - 1].end_time)}</td>
                      <td className="px-3 py-2" colSpan={3}>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {Object.entries(counts).map(([st, n]) => <span key={st} className="inline-flex items-center gap-1 text-[10px] text-slate-600"><Pill label={st} color={TS_STYLES[st] || "#9CA3AF"} />×{n}</span>)}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold border rounded-full px-2 py-0.5 bg-violet-50 text-violet-700 border-violet-200" data-testid={`ts-group-badge-${group.id}`}>
                          <Link2 className="h-3 w-3" />Linked · {legs.length} shifts
                          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                        </span>
                      </td>
                    </tr>
                  );
                }
                const rin = s.payroll_start ? dayjs(s.payroll_start) : roundQuarter(s.actual_clock_in), rout = s.payroll_end ? dayjs(s.payroll_end) : roundQuarter(s.actual_clock_out);
                const noClockOut = !s.actual_clock_out;
                const noNote = !s.notes_ready;
                // Sleepover window duration helper (handles overnight where end < start)
                const sleepdur = (st, en) => {
                  if (!st || !en) return null;
                  let a = dayjs(st), b = dayjs(en);
                  if (b.isBefore(a)) b = b.add(1, "day");
                  const h = b.diff(a, "minute") / 60;
                  return Number.isInteger(h) ? `${h}h` : `${h.toFixed(1)}h`;
                };
                return (
                  <tr key={s.name} className={`border-b border-slate-50 ${child ? "bg-violet-50/20 hover:bg-violet-50/50" : "hover:bg-slate-50/50"}`} data-testid={`ts-row-${s.name}`}>
                    <td className={`px-3 py-2 ${child ? "pl-6 border-l-2 border-violet-300" : ""}`}>{child && <div className="text-[10px] font-semibold text-violet-600">Leg {leg}/{legCount}</div>}<div className="font-mono text-[11px] text-slate-500">{s.shift_name || s.name}</div><div className="text-slate-400 text-[10px]">{fmtDate(s.shift_date)}</div>{s.multi_worker && <div className="text-[10px] font-semibold text-violet-600" data-testid={`ts-multi-${s.name}`}>Multi-worker shift</div>}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-800">{s.support_worker_name || "—"}</div>
                      <div className="text-slate-400 text-[10px]">{s.participant_name}</div>
                      {noNote ? <div className="text-[10px] text-red-500 font-semibold mt-0.5" data-testid={`ts-note-${s.name}`}>Notes not submitted</div>
                        : <div className="text-[10px] text-emerald-600 font-semibold mt-0.5" data-testid={`ts-note-${s.name}`}>Notes submitted ✓</div>}
                      {s.has_incident ? <div className="text-[10px] text-red-600 font-semibold mt-0.5 flex items-center gap-1" data-testid={`ts-incident-${s.name}`}><ShieldAlert className="h-3 w-3" />Incident reported</div> : null}
                      {(s.clock_in_out_of_range || s.clock_out_out_of_range) ? <div className="text-[10px] text-orange-600 font-semibold mt-0.5 flex items-center gap-1" data-testid={`ts-geo-${s.name}`}><MapPin className="h-3 w-3" />Clock out of range</div> : null}
                      {s.ai_recommendation ? <div className="mt-0.5 flex flex-wrap items-center gap-1" data-testid={`ts-ai-${s.name}`}><AiReviewBadge rec={s.ai_recommendation} confidence={s.ai_confidence} /><AiAutoBadge action={s.ai_auto_action} /></div> : null}
                      {s.is_sleepover && (
                        <div className="mt-1 flex flex-wrap items-center gap-1" data-testid={`ts-sleepover-${s.name}`}>
                          <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold bg-indigo-100 text-indigo-700">
                            <Moon className="h-2.5 w-2.5" />Sleepover
                          </span>
                          {s.sleep_start_time && s.sleep_end_time && (
                            <span className="text-[10px] text-slate-500">
                              {fmtTime(s.sleep_start_time)}–{fmtTime(s.sleep_end_time)}
                              {sleepdur(s.sleep_start_time, s.sleep_end_time) && <> · {sleepdur(s.sleep_start_time, s.sleep_end_time)} window</>}
                            </span>
                          )}
                          {s.awake_minutes > 0 ? (
                            <span className={`text-[10px] font-semibold ${s.awake_approved ? "text-amber-600" : "text-amber-500"}`} data-testid={`ts-awake-${s.name}`}>
                              · {Math.round(s.awake_minutes)}m awake{s.awake_approved ? " ✓" : " (pending)"}
                            </span>
                          ) : (
                            <span className="text-[10px] text-slate-400" data-testid={`ts-awake-${s.name}`}>· No awake time</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 tabular-nums text-slate-600">{fmtTime(s.start_time)}–{fmtTime(s.end_time)}</td>
                    <td className="px-3 py-2 tabular-nums text-slate-600">{fmtTime(s.actual_clock_in)}–{noClockOut ? <span className="text-red-500 font-semibold">missing</span> : fmtTime(s.actual_clock_out)}</td>
                    <td className="px-3 py-2 tabular-nums font-semibold text-slate-800">
                      {rin ? rin.format("h:mm A") : "—"}–{rout ? rout.format("h:mm A") : "—"}
                      {s.timesheet_status === "Approved" && s.payroll_hours ? (
                        <div className="text-[10px] font-semibold text-emerald-700" data-testid={`ts-paid-${s.name}`}>
                          {s.payroll_hours}h paid
                        </div>
                      ) : null}
                      {s.timesheet_status === "Approved" && (
                        s.payroll_status === "Exported"
                          ? <div className="text-[10px] font-semibold text-slate-500" data-testid={`ts-payroll-${s.name}`}>Paid · {s.payroll_ref || "exported to payroll"}</div>
                          : <div className="text-[10px] font-semibold text-amber-600" data-testid={`ts-payroll-${s.name}`}>Not yet exported to payroll</div>
                      )}
                      {s.timesheet_status === "Approved" && ["Invoiced", "Submitted"].includes(s.custom_invoice_status) && (
                        <div className="text-[10px] font-semibold text-slate-500" data-testid={`ts-invoice-${s.name}`}>Invoiced{s.custom_invoice_ref ? ` · ${s.custom_invoice_ref}` : ""}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Pill label={s.timesheet_status} color={TS_STYLES[s.timesheet_status] || "#9CA3AF"} />
                      {s.timesheet_flag_reason && <div className="text-[10px] text-red-500 mt-0.5 max-w-[160px]">“{s.timesheet_flag_reason}”</div>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => openDetail(s.name, s.ts_key)} data-testid={`ts-view-${s.name}`}>
                          <Eye className="h-3 w-3 mr-1" />View
                        </Button>
                        {can.has("timesheets.unapprove") && s.timesheet_status === "Approved" && !closedReason(s) && (
                          <Button size="sm" variant="outline" className="h-7 text-[11px] text-amber-700 border-amber-300" onClick={() => { setUnapprove(s); setUnapproveReason(""); }} data-testid={`ts-unapprove-${s.name}`}>
                            <Undo2 className="h-3 w-3 mr-1" />Unapprove
                          </Button>
                        )}
                        {can.has("timesheets.approve") && s.timesheet_status !== "Approved" && (<>
                          {(s.awake_undecided || s.pending_expenses > 0) ? (
                            <Button size="sm" variant="outline" className="h-7 text-[11px] text-amber-700 border-amber-300" onClick={() => openDetail(s.name, s.ts_key)}
                              data-testid={`ts-review-${s.name}`}
                              title={`Approve or reject ${[s.awake_undecided && "the awake time", s.pending_expenses > 0 && "the expense claims"].filter(Boolean).join(" and ")} before approving`}>
                              <Eye className="h-3 w-3 mr-1" />Review
                            </Button>
                          ) : (
                            <Button size="sm" className="h-7 text-[11px]" style={{ backgroundColor: "#10B981" }} onClick={() => openDetail(s.name, s.ts_key)} data-testid={`ts-approve-${s.name}`} disabled={noClockOut || noNote} title={noClockOut ? "No clock-out — flag instead" : noNote ? "Worker hasn't submitted notes — cannot approve" : ""}>
                              <Check className="h-3 w-3 mr-1" />Approve
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="h-7 text-[11px] text-red-600 border-red-200" onClick={() => { setFlag(s); setReason(""); }} data-testid={`ts-flag-${s.name}`}>
                            <Flag className="h-3 w-3 mr-1" />Flag
                          </Button>
                        </>)}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Shift detail review */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="w-[96vw] max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="ts-detail-dialog">
          <DialogHeader><DialogTitle className="text-base">{detail?.title || detail?.shift_name || detail?.name}{detail?.multi_worker || detail?.timesheet_worker ? ` · ${detail.support_worker_name}` : ""} — shift review</DialogTitle></DialogHeader>
          {detail && (
            <div className="grid gap-5 text-xs lg:grid-cols-2 lg:items-start">
              <div className="space-y-3 min-w-0" data-testid="detail-col-left">
              <AiReviewPanel name={detail.name} ai={detail}
                onRefreshed={async () => { const { shift } = await api.shift(detail.name); setDetail((d) => ({ ...shift, ts_key: d?.ts_key })); load(); }} />
              <div className="grid grid-cols-2 gap-2">
                <Info label="Participant" value={detail.participant_name} />
                <Info label="Worker" value={detail.support_worker_name} />
                <Info label="KM" value={`${detail.km_claimed || 0} / ${detail.km_authorised || 0} auth${detail.km_start_odometer != null ? ` (${detail.km_start_odometer}→${detail.km_end_odometer})` : ""}`} />
              </div>
              {review && <HoursPanel shift={detail} review={review} setReview={setReview} summary={summary} editable={editable} />}
              {detail.is_sleepover && review && (
                <AwakePanel shift={detail} entries={awakeEntries} review={review} setReview={setReview} editable={editable}
                  onReturn={() => openSendBack({ awake: true })} />
              )}
              {!!detail.km_extension_requested && (
                <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-800" data-testid="detail-km-ext">
                  KM over authorised — extension requested, spoke with <b>{detail.km_extension_contact || "?"}</b>
                </div>
              )}
              {detailMeta.km && (
                <ManualKmForm shiftName={detail.name} km={detailMeta.km} canEdit={can.has("timesheets.edit")} canApprove={can.has("timesheets.approve")}
                  onSaved={async () => { await loadExpenses(detail.name); const { shift } = await api.shift(detail.name); setDetail((d) => ({ ...shift, ts_key: d?.ts_key })); load(); }} />
              )}
              <div data-testid="detail-geofence">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 flex items-center gap-1"><MapPin className="h-3 w-3" />Location verification</div>
                {detail.end_address_enabled && detail.end_address && (
                  <div className="text-[11px] text-slate-600 mb-1.5">Ends at: <b>{detail.end_address}</b></div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {[["in", "Clock in", detail.clock_in_latitude, detail.clock_in_longitude, detail.clock_in_distance, detail.clock_in_out_of_range],
                    ["out", "Clock out", detail.clock_out_latitude, detail.clock_out_longitude, detail.clock_out_distance, detail.clock_out_out_of_range]]
                    .map(([id, lbl, lat, lng, dist, out]) => (
                      <div key={id} className="rounded-md border border-slate-200 p-2">
                        <div className="text-[10px] font-semibold text-slate-500">{lbl}</div>
                        {lat && lng ? (<>
                          <div className={`text-[11px] font-semibold mt-0.5 ${out ? "text-orange-600" : "text-emerald-600"}`} data-testid={`detail-geo-${id}`}>
                            {dist != null ? fmtDistance(dist) : "—"} {out ? "· out of range" : dist != null ? "· in range" : ""}
                          </div>
                          <LocationMap lat={lat} lng={lng} label={lbl} height={110} className="mt-1.5" />
                        </>) : <div className="text-[11px] text-slate-400 mt-0.5">No GPS captured</div>}
                      </div>
                    ))}
                </div>
                {detail.shift_latitude && detail.shift_longitude && (
                  <div className="mt-2 rounded-md border border-slate-200 p-2">
                    <div className="text-[10px] font-semibold text-slate-500">Shift address</div>
                    <LocationMap lat={detail.shift_latitude} lng={detail.shift_longitude} label="Shift address" height={120} className="mt-1.5" />
                  </div>
                )}
                {detail.geofence_unverifiable && <div className="text-[11px] text-slate-500 mt-1">Address could not be geocoded — distance not verified.</div>}
              </div>
              </div>
              <div className="space-y-3 min-w-0" data-testid="detail-col-right">
              {detailExpenses.length > 0 && review && (
                <ExpensePanel shiftName={detail.name} expenses={detailExpenses} meta={detailMeta}
                  review={review} setReview={setReview} editable={editable} />
              )}
              {detailReturn && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5" data-testid="detail-return-summary">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 flex items-center gap-1"><RotateCcw className="h-3 w-3" />Sent back to worker</div>
                    {can.has("timesheets.edit") && (
                      <button type="button" onClick={() => openSendBack()} data-testid="detail-return-change"
                        className="text-[11px] font-semibold text-amber-700 hover:text-amber-900">Change what's sent back</button>
                    )}
                  </div>
                  <div className="text-xs text-slate-700 mt-1" data-testid="detail-return-parts">
                    {detailReturn.legacy ? "Everything (notes, KM, expenses, incidents and tasks)" : [
                      ...detailNotes.filter((n) => detailReturn.notes?.includes(n.name)).map((n) => `Notes — ${n.participant_name || n.participant}`),
                      ...returnLabels.filter(([k]) => detailReturn[k]).map(([, l]) => l),
                    ].join(" · ") || "No parts"}
                  </div>
                  {detailReturn.reason && <div className="text-[11px] text-amber-700 mt-1">“{detailReturn.reason}”</div>}
                  <div className="text-[10px] text-slate-500 mt-1">The worker can always report a new incident while the timesheet is returned.</div>
                </div>
              )}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Shift notes</div>
                {detailNotes.length === 0 ? (
                  <div className="rounded-md bg-slate-50 border border-slate-200 p-2 text-red-500" data-testid="detail-note">No shift notes recorded</div>
                ) : (
                  <div className="space-y-1.5" data-testid="detail-note">
                    {detailNotes.map((n) => (
                      <div key={n.name} className="rounded-md bg-slate-50 border border-slate-200 p-2">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="font-medium text-slate-800">{n.participant_name} <span className="text-slate-400 font-normal">· {n.support_worker_name}</span></span>
                          <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded ${n.status === "Submitted" ? "bg-emerald-100 text-emerald-700" : n.status === "Returned" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{n.status}</span>
                        </div>
                        <NoteSections n={n} />
                        {n.returned_reason && <div className="text-[10px] text-amber-600 mt-1">Returned: “{n.returned_reason}”</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {detail.tasks?.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Tasks</div>
                  <div className="space-y-1.5">
                    {detail.tasks.map((t, i) => {
                      const done = (t.completion_status || (t.completed ? "Completed" : "")) === "Completed";
                      const notDone = t.completion_status === "Not Completed";
                      return (
                        <div key={i} data-testid={`detail-task-${i}`} className="text-slate-700">
                          <div className="flex items-center gap-2">
                            <span className={done ? "text-emerald-600" : notDone ? "text-red-500" : "text-slate-300"}>{done ? "✓" : notDone ? "✗" : "○"}</span>
                            <span className={done ? "" : notDone ? "text-slate-700" : "text-slate-400"}>{t.title}</span>
                            {done && <span className="text-[9px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">Completed</span>}
                            {notDone && <span className="text-[9px] font-semibold uppercase tracking-wide text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">Not completed</span>}
                            {!done && !notDone && <span className="text-[9px] font-semibold uppercase tracking-wide text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">No response</span>}
                          </div>
                          {notDone && t.not_done_reason && (
                            <div className="ml-6 mt-0.5 text-[11px] text-red-600" data-testid={`detail-task-${i}-reason`}>Reason: {t.not_done_reason}</div>
                          )}
                          {t.notes && <div className="ml-6 text-[11px] text-slate-500">{t.notes}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {detailIncidents.length > 0 ? (
                <div className="rounded-md bg-red-50 border border-red-200 p-2" data-testid="detail-incident">
                  <div className="flex items-center gap-1.5 text-red-700 font-semibold mb-1.5"><ShieldAlert className="h-3.5 w-3.5" />{detailIncidents.length} incident{detailIncidents.length === 1 ? "" : "s"} reported</div>
                  <div className="space-y-1.5">
                    {detailIncidents.map((inc) => (
                      <div key={inc.name} className="rounded bg-white border border-red-100 p-2" data-testid={`detail-incident-${inc.name}`}>
                        {can.has("incidents.view") ? (
                          <Link to={`/incidents/${inc.name}`} className="block hover:opacity-80">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-slate-800">{inc.name} <span className="font-normal text-slate-500">· {(inc.types || []).join(", ")}</span></span>
                              <span className="text-[10px] font-semibold text-red-700 flex items-center gap-1">Manage incident <ExternalLink className="h-3 w-3" /></span>
                            </div>
                            <div className="text-[10px] text-slate-500 mt-0.5">{inc.incident_datetime} · {inc.severity} · {inc.status}{inc.reportable ? " · NDIS reportable" : ""}</div>
                          </Link>
                        ) : (
                          <div>
                            <div className="font-semibold text-slate-800">{inc.name} <span className="font-normal text-slate-500">· {(inc.types || []).join(", ")}</span></div>
                            <div className="text-[10px] text-slate-500 mt-0.5">{inc.incident_datetime} · {inc.severity} · {inc.status}{inc.reportable ? " · NDIS reportable" : ""}</div>
                          </div>
                        )}
                        {can.has("incidents.manage") && inc.status !== "Returned" && inc.source !== "Office" && (
                          <button data-testid={`detail-incident-return-${inc.name}`}
                            onClick={async () => {
                              const reason = window.prompt("Return this incident to the worker for changes. Reason:");
                              if (!reason || !reason.trim()) return;
                              try { await api.returnIncident(inc.name, reason.trim()); toast.success("Incident returned to worker"); setDetailIncidents(await api.shiftIncidents(detail.shift_name || detail.name)); }
                              catch (e) { toast.error(e.response?.data?.detail || "Could not return incident"); }
                            }}
                            className="mt-1.5 text-[10px] font-semibold text-amber-700 hover:text-amber-900 flex items-center gap-1"><RotateCcw className="h-3 w-3" /> Return to worker</button>
                        )}
                        {inc.status === "Returned" && <div className="mt-1 text-[10px] font-semibold text-amber-600">Returned to worker for changes</div>}
                      </div>
                    ))}
                  </div>
                </div>
              ) : <div className="text-slate-400">No incident reported on this shift.</div>}
              {can.has("incidents.create") && (
                <Link to={`/incidents/new?shift=${encodeURIComponent(detail.shift_name || detail.name)}`} data-testid="detail-incident-new"
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-700 hover:text-red-900">
                  <Plus className="h-3 w-3" /> Log an incident for this shift
                </Link>
              )}
              {detail.audit_entries?.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Audit trail</div>
                    <Button variant="outline" size="sm" className="h-7 text-[11px]" data-testid="view-note-history"
                      onClick={async () => {
                        try { const h = await api.shiftNotesHistory(detail.ts_key || detail.name); setHistory({ shift: detail.shift_name || detail.name, notes: h }); }
                        catch { toast.error("Could not load note history"); }
                      }}>
                      <History className="h-3.5 w-3.5 mr-1" /> View historical shift notes
                    </Button>
                  </div>
                  <div className="border-l-2 border-slate-100 pl-3 space-y-1">
                    {[...detail.audit_entries].reverse().map((a, i) => (
                      <div key={i} className="text-[11px] text-slate-600"><span className="font-semibold text-slate-800">{a.action}</span> · {fmtLog(a.timestamp)} · {a.performed_by}{a.reason ? ` — “${a.reason}”` : ""}{a.details ? <div className="text-slate-400">{a.details}</div> : null}</div>
                    ))}
                  </div>
                </div>
              )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDetail(null)}>Close</Button>
            {can.has("timesheets.edit") && detail && (detailNotes.some((n) => n.status === "Submitted") || ["Pending Review", "Flagged"].includes(detail.timesheet_status)
              || (detail.timesheet_status === "Returned" && detailReturn)) && (
              <Button size="sm" variant="outline" className="text-amber-700 border-amber-300" data-testid="detail-sendback"
                onClick={() => openSendBack()}>
                <RotateCcw className="h-3.5 w-3.5 mr-1" />{detail.timesheet_status === "Returned" ? "Change what's sent back" : "Send back to worker"}
              </Button>
            )}
            {can.has("timesheets.unapprove") && detail && detail.timesheet_status === "Approved" && closedReason(detail) && (
              <span className="text-[11px] text-slate-500 self-center mr-1" data-testid="ts-detail-locked">{closedReason(detail)}</span>
            )}
            {can.has("timesheets.unapprove") && detail && detail.timesheet_status === "Approved" && !closedReason(detail) && (
              <Button variant="outline" size="sm" className="text-amber-700 border-amber-300" onClick={() => { setUnapprove(detail); setUnapproveReason(""); }} data-testid="ts-detail-unapprove">
                <Undo2 className="h-3.5 w-3.5 mr-1" />Unapprove
              </Button>
            )}
            {editable && can.has("timesheets.approve") && (() => {
              const parts = detail.shift_participants?.length ? detail.shift_participants.map((p) => p.participant) : (detail.participant ? [detail.participant] : []);
              const submitted = new Set(detailNotes.filter((n) => n.status === "Submitted").map((n) => n.participant));
              const notesReady = parts.length > 0 && parts.every((p) => submitted.has(p));
              const pend = pendingDecisions(review, detailExpenses);
              const why = !detail.actual_clock_out && !review?.pay_end ? "No clock-out — flag instead"
                : !notesReady ? "Worker hasn't submitted notes — cannot approve"
                : pend.length ? `Approve or reject ${pend.join(" and ")} first` : "";
              return (<>
                {why && <span className="text-[11px] text-amber-700 self-center mr-1" data-testid="detail-approve-why">{why}</span>}
                <Button size="sm" style={{ backgroundColor: "#10B981" }} data-testid="detail-approve" onClick={doApprove}
                  disabled={!!why || approving} title={why}>
                  {approving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Check className="h-3.5 w-3.5 mr-1" />}Approve
                </Button>
              </>);
            })()}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Overdue actions: record times / dismiss / cancel the shift */}
      {/* Unapprove dialog */}
      <Dialog open={!!unapprove} onOpenChange={(v) => !v && setUnapprove(null)}>
        <DialogContent data-testid="unapprove-dialog">
          <DialogHeader><DialogTitle className="text-base">Unapprove timesheet</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">
            {unapprove?.name} · {unapprove?.support_worker_name || "—"}. This returns the shift to Pending Review so it can be
            edited and re-approved. The reason is recorded in the audit trail.
          </p>
          <Textarea value={unapproveReason} rows={3} placeholder="Reason for unapproving…" data-testid="unapprove-reason"
            onChange={(e) => setUnapproveReason(e.target.value)} className="text-sm" />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setUnapprove(null)}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: "#d97706" }} onClick={doUnapprove} data-testid="unapprove-confirm">Unapprove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!overdueAction} onOpenChange={(v) => !v && setOverdueAction(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="overdue-action-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">
              {overdueAction?.mode === "times" ? "Record clock times"
                : overdueAction?.mode === "dismiss" ? "Dismiss from overdue"
                : "Cancel this shift"}
            </DialogTitle>
          </DialogHeader>
          {overdueAction && (
            <div className="text-[11px] text-slate-500 -mt-2">
              {overdueAction.shift.name} · {overdueAction.shift.support_worker_name || "—"} ·
              {" "}{overdueAction.shift.participant_name} · {fmtDate(overdueAction.shift.shift_date)} ·
              {" "}{fmtTime(overdueAction.shift.start_time)}–{fmtTime(overdueAction.shift.end_time)}
            </div>
          )}
          {overdueAction?.mode === "times" && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500">
                For a worker who never clocked in or out. The shift moves to Pending Review for
                normal approval and the change is recorded against the shift.
              </p>
              <div className="grid sm:grid-cols-2 gap-2">
                <div>
                  <div className="text-[10px] text-slate-500 mb-0.5">Clock in</div>
                  <Input type="datetime-local" value={odTimes.clock_in} data-testid="overdue-clock-in"
                    onChange={(e) => setOdTimes((t) => ({ ...t, clock_in: e.target.value }))} className="h-9 text-sm" />
                </div>
                <div>
                  <div className="text-[10px] text-slate-500 mb-0.5">Clock out</div>
                  <Input type="datetime-local" value={odTimes.clock_out} data-testid="overdue-clock-out"
                    onChange={(e) => setOdTimes((t) => ({ ...t, clock_out: e.target.value }))} className="h-9 text-sm" />
                </div>
              </div>
              <Textarea value={odTimes.reason} rows={2} data-testid="overdue-times-reason"
                onChange={(e) => setOdTimes((t) => ({ ...t, reason: e.target.value }))}
                placeholder="Reason (required) — e.g. worker confirmed times by phone" className="text-sm" />
            </div>
          )}
          {overdueAction?.mode === "dismiss" && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500">
                Takes the entry off the overdue list. The shift itself isn't changed or deleted, and
                it stays visible under “Show dismissed entries”.
              </p>
              <select value={odDismiss.preset} data-testid="overdue-dismiss-reason"
                onChange={(e) => setOdDismiss((d) => ({ ...d, preset: e.target.value }))}
                className="w-full h-9 text-sm rounded-md border border-slate-200 bg-white px-2 text-slate-700">
                {DISMISS_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <Textarea value={odDismiss.note} rows={2} data-testid="overdue-dismiss-note"
                onChange={(e) => setOdDismiss((d) => ({ ...d, note: e.target.value }))}
                placeholder="Extra detail (optional)" className="text-sm" />
            </div>
          )}
          {overdueAction?.mode === "cancel" && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500">
                Use this when the shift never went ahead. A charged cancellation bills the full
                shift and pays the assigned worker.
              </p>
              <Textarea value={odCancel.reason} rows={2} data-testid="overdue-cancel-reason"
                onChange={(e) => setOdCancel((c) => ({ ...c, reason: e.target.value }))}
                placeholder="Cancellation reason (required)" className="text-sm" />
              <select
                data-testid="overdue-cancel-initiator"
                value={odCancel.initiator}
                onChange={(e) => setOdCancel((c) => ({ ...c, initiator: e.target.value }))}
                className="w-full rounded-md border border-slate-200 text-sm px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
              >
                <option value="">— Who initiated this cancellation? —</option>
                <option value="Participant initiated">Participant initiated</option>
                <option value="Office initiated">Office initiated</option>
                <option value="Mutual agreement">Mutual agreement</option>
                <option value="Other">Other</option>
              </select>
              <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer" data-testid="overdue-cancel-charge">
                <input type="checkbox" checked={odCancel.charge} className="h-4 w-4"
                  onChange={(e) => setOdCancel((c) => ({ ...c, charge: e.target.checked }))} />
                Charge the participant (short notice) and pay the worker
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOverdueAction(null)}>Cancel</Button>
            <Button size="sm" disabled={odBusy} onClick={submitOverdueAction} data-testid="overdue-action-confirm"
              style={{ backgroundColor: overdueAction?.mode === "cancel" ? "#bf3b4f" : "#10B981" }}>
              {odBusy ? <Loader2 className="h-4 w-4 animate-spin" />
                : overdueAction?.mode === "times" ? "Save times"
                : overdueAction?.mode === "dismiss" ? "Dismiss entry" : "Cancel shift"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Flag dialog */}
      <Dialog open={!!flag} onOpenChange={(v) => !v && setFlag(null)}>
        <DialogContent data-testid="flag-dialog">
          <DialogHeader><DialogTitle className="text-base">Flag for human review</DialogTitle></DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why does this need review? (e.g., no clock-out, large variance)" className="text-sm min-h-[90px]" data-testid="flag-reason" />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setFlag(null)}>Cancel</Button>
            <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={doFlag} data-testid="flag-confirm">Flag</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk shift-notes download dialog */}
      <Dialog open={notesDl.open} onOpenChange={(v) => !v && setNotesDl((s) => ({ ...s, open: false }))}>
        <DialogContent data-testid="bulk-notes-dialog">
          <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><Download className="h-4 w-4" />Download shift notes</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Export all submitted shift notes for a date range as a single PDF. Leave the participant blank for everyone.</p>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-[11px] font-medium text-slate-600">From</label>
              <Input type="date" value={notesDl.date_from} onChange={(e) => setNotesDl((s) => ({ ...s, date_from: e.target.value }))} className="h-9 text-xs mt-1" data-testid="bulk-notes-from" /></div>
            <div><label className="text-[11px] font-medium text-slate-600">To</label>
              <Input type="date" value={notesDl.date_to} onChange={(e) => setNotesDl((s) => ({ ...s, date_to: e.target.value }))} className="h-9 text-xs mt-1" data-testid="bulk-notes-to" /></div>
          </div>
          <div><label className="text-[11px] font-medium text-slate-600">Participant (optional)</label>
            <div className="mt-1">
              <Combobox testid="bulk-notes-participant" placeholder="All participants" value={notesDl.participant || null}
                options={notesDlParticipants.map((p) => ({ value: p.name, label: p.display_name || p.name, hint: p.name }))}
                onChange={(v) => setNotesDl((s) => ({ ...s, participant: v || "" }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setNotesDl((s) => ({ ...s, open: false }))}>Cancel</Button>
            <Button size="sm" style={{ backgroundColor: "#bf3b4f" }} onClick={downloadNotes} disabled={notesDlBusy} data-testid="bulk-notes-confirm">
              {notesDlBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send back to worker dialog — partial return */}
      <Dialog open={!!sendBack} onOpenChange={(v) => !v && setSendBack(null)}>
        <DialogContent data-testid="sendback-dialog">
          <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><RotateCcw className="h-4 w-4" />{sendBack?.timesheet_status === "Returned" ? "Change what's sent back" : "Send back to worker"}</DialogTitle></DialogHeader>
          {sendBack?.timesheet_status === "Returned" ? (
            <p className="text-xs text-slate-500 -mt-2">This replaces what you sent back before. Untick a part to lock it again (anything the worker has already changed in it stays as they left it). The worker gets a new email with your reason.</p>
          ) : (
            <p className="text-xs text-slate-500 -mt-2">Choose exactly what to unlock. Only the parts you tick go back to the worker; everything else stays locked. The timesheet moves to the <b>Returned</b> tab until they resubmit.</p>
          )}

          <div className="space-y-1.5 border border-slate-200 rounded-lg p-2.5" data-testid="sendback-parts">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">Shift notes</div>
            {returnableNotes.length === 0 ? (
              <div className="text-[11px] text-slate-400">No submitted notes to return.</div>
            ) : returnableNotes.map((n) => (
              <label key={n.name} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer" data-testid={`sendback-note-${n.participant}`}>
                <input type="checkbox" className="accent-[#bf3b4f]" checked={returnParts.notes.includes(n.name)}
                  onChange={(e) => setReturnParts((p) => ({ ...p, notes: e.target.checked ? [...p.notes, n.name] : p.notes.filter((x) => x !== n.name) }))} />
                Notes — {n.participant_name || n.participant}
              </label>
            ))}
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mt-2 mb-1">Other parts</div>
            {returnLabels.filter(([k]) => (k !== "awake" || sendBack?.is_sleepover) && (k !== "tasks" || sendBack?.tasks?.length || returnParts.tasks)).map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer" data-testid={`sendback-part-${k}`}>
                <input type="checkbox" className="accent-[#bf3b4f]" checked={returnParts[k]}
                  onChange={(e) => setReturnParts((p) => ({ ...p, [k]: e.target.checked }))} />
                {label}
              </label>
            ))}
          </div>

          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="What needs to change? (e.g., add more detail on the outing, fix the KM)" className="text-sm min-h-[80px]" data-testid="sendback-reason" />
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setSendBack(null)}>Cancel</Button>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={doReturn} data-testid="sendback-confirm">{sendBack?.timesheet_status === "Returned" ? "Update what's sent back" : "Send back selected"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Historical shift notes dialog */}
      <Dialog open={!!history} onOpenChange={(v) => !v && setHistory(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="note-history-dialog">
          <DialogHeader><DialogTitle className="text-base flex items-center gap-2"><History className="h-4 w-4" />Historical shift notes</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Every version the worker submitted (earliest first), including any resubmissions after a return.</p>
          <div className="space-y-3">
            {(history?.notes || []).every((n) => !n.versions?.length) && (
              <div className="text-xs text-slate-400 text-center py-6" data-testid="note-history-empty">No earlier versions recorded for this shift.</div>
            )}
            {(history?.notes || []).map((n) => (n.versions?.length ? (
              <div key={n.name} data-testid={`note-history-${n.participant}`}>
                <div className="text-[11px] font-bold text-slate-700 mb-1">{n.participant_name || n.participant}</div>
                <div className="space-y-2">
                  {n.versions.map((v, i) => (
                    <div key={i} className="rounded-md bg-slate-50 border border-slate-200 p-2 text-[11px] text-slate-700">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-slate-800">Version {v.version}{v.was_resubmission ? " · resubmission" : ""}</span>
                        <span className="text-slate-400">{v.submitted_at ? fmtLog(v.submitted_at) : ""}</span>
                      </div>
                      {v.was_resubmission && v.return_reason && <div className="text-[10px] text-amber-600 mb-1">Returned before: “{v.return_reason}”</div>}
                      {v.events && <div><span className="text-slate-400">Events: </span>{v.events}</div>}
                      {v.mood && <div><span className="text-slate-400">Mood: </span>{v.mood}</div>}
                      {v.urgent_notes && <div className="text-red-600">Important: {v.urgent_notes}</div>}
                      {v.contacted_office && <div className="text-[#bf3b4f]">Contacted office{v.office_contact_reason ? `: ${v.office_contact_reason}` : ""}</div>}
                    </div>
                  ))}
                </div>
              </div>
            ) : null))}
          </div>
          <DialogFooter><Button variant="outline" size="sm" onClick={() => setHistory(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div>
    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
    <div className="text-slate-800">{value || "—"}</div>
  </div>
);
